import { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import NewQuote from "./pages/NewQuote";
import QuoteDetail from "./pages/QuoteDetail";
import QuotesList from "./pages/QuotesList";
import Settings from "./pages/Settings";
import Materials from "./pages/Materials";
import OnboardingWizard from "./pages/OnboardingWizard";
import Pricing from "./pages/Pricing";
import PaymentSuccess from "./pages/PaymentSuccess";
import AdminDashboard from "./pages/AdminDashboard";
import ResetPassword from "./pages/ResetPassword";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Subscription from "./pages/Subscription";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth context
export const AuthContext = {
  user: null,
  setUser: () => {},
  logout: () => {},
  API
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        navigate("/", { replace: true });
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        // Use XMLHttpRequest to avoid Emergent preview script fetch interception
        const result = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("POST", `${API}/auth/session`);
          xhr.setRequestHeader("Content-Type", "application/json");
          xhr.setRequestHeader("X-Session-ID", sessionId);
          xhr.withCredentials = true;
          
          xhr.onload = () => {
            resolve({
              ok: xhr.status >= 200 && xhr.status < 300,
              status: xhr.status,
              text: xhr.responseText
            });
          };
          
          xhr.onerror = () => reject(new Error("Network error"));
          xhr.ontimeout = () => reject(new Error("Request timeout"));
          xhr.timeout = 30000; // 30 second timeout
          
          xhr.send(JSON.stringify({}));
        });

        if (!result.ok) {
          throw new Error("Auth failed");
        }

        const user = JSON.parse(result.text);
        window.history.replaceState(null, "", "/dashboard");
        navigate("/dashboard", { replace: true, state: { user } });
      } catch (error) {
        console.error("Auth error:", error);
        navigate("/", { replace: true });
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If user was passed from AuthCallback, use it
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      // Check if onboarding needed
      if (!location.state.user.onboarding_completed) {
        setShowOnboarding(true);
      }
      return;
    }

    const checkAuth = async () => {
      try {
        // Use XMLHttpRequest to avoid Emergent preview script fetch interception
        const result = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("GET", `${API}/auth/me`);
          xhr.withCredentials = true;
          
          xhr.onload = () => {
            resolve({
              ok: xhr.status >= 200 && xhr.status < 300,
              status: xhr.status,
              text: xhr.responseText
            });
          };
          
          xhr.onerror = () => reject(new Error("Network error"));
          xhr.ontimeout = () => reject(new Error("Request timeout"));
          xhr.timeout = 15000;
          
          xhr.send();
        });

        if (!result.ok) {
          throw new Error("Not authenticated");
        }

        const userData = JSON.parse(result.text);
        setUser(userData);
        setIsAuthenticated(true);
        // Check if onboarding needed
        if (!userData.onboarding_completed) {
          setShowOnboarding(true);
        }
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/", { replace: true });
      }
    };

    checkAuth();
  }, [navigate, location.state]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // Show onboarding wizard if not completed
  if (showOnboarding) {
    return (
      <OnboardingWizard 
        user={user} 
        setUser={setUser} 
        API={API}
        onComplete={() => setShowOnboarding(false)}
      />
    );
  }

  // Pass user to children
  return children({ user, setUser, API });
};

// Optional auth - tries to get user but doesn't require authentication
const OptionalAuth = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Use XMLHttpRequest to avoid Emergent preview script fetch interception
        const result = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("GET", `${API}/auth/me`);
          xhr.withCredentials = true;
          
          xhr.onload = () => {
            resolve({
              ok: xhr.status >= 200 && xhr.status < 300,
              status: xhr.status,
              text: xhr.responseText
            });
          };
          
          xhr.onerror = () => reject(new Error("Network error"));
          xhr.ontimeout = () => reject(new Error("Request timeout"));
          xhr.timeout = 15000;
          
          xhr.send();
        });
        
        if (result.ok) {
          const userData = JSON.parse(result.text);
          setUser(userData);
        }
      } catch (error) {
        // Not logged in - that's fine
      } finally {
        setIsChecking(false);
      }
    };

    checkAuth();
  }, []);

  if (isChecking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return children({ user, setUser, API });
};

function AppRouter() {
  const location = useLocation();

  // Check for session_id in URL fragment BEFORE routing
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {(props) => <Dashboard {...props} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/quote/new"
        element={
          <ProtectedRoute>
            {(props) => <NewQuote {...props} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/quote/:quoteId"
        element={
          <ProtectedRoute>
            {(props) => <QuoteDetail {...props} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/quotes"
        element={
          <ProtectedRoute>
            {(props) => <QuotesList {...props} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            {(props) => <Settings {...props} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/materials"
        element={
          <ProtectedRoute>
            {(props) => <Materials {...props} />}
          </ProtectedRoute>
        }
      />
      <Route 
        path="/pricing" 
        element={
          <OptionalAuth>
            {(props) => <Pricing {...props} />}
          </OptionalAuth>
        } 
      />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute>
            {(props) => <Subscription {...props} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/success"
        element={
          <ProtectedRoute>
            {(props) => <PaymentSuccess {...props} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            {(props) => <AdminDashboard {...props} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/reset-password"
        element={<ResetPassword />}
      />
      <Route
        path="/terms"
        element={<Terms />}
      />
      <Route
        path="/privacy"
        element={<Privacy />}
      />
    </Routes>
  );
}

function App() {
  return (
    <>
      <div className="noise-overlay" />
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </>
  );
}

export default App;
